import { useCallback } from "react"

import {
  Questionnaire,
  QuestionnaireResponsePair,
  UserPlacement,
} from "@/types/questionnaire/models"

import { getOptionsPlacements } from "../utils/optionsPlacementUtils"
import {
  AnswerPlacementPayload,
  normalizeOptionText,
  toApiPlacement,
} from "../utils/placementUtils"

type QuestionUpdater = (
  q: QuestionnaireResponsePair,
) => QuestionnaireResponsePair

export const useQuestionnaireHandlers = (
  questionnaireData: Questionnaire,
  questionnaireId: string,
  updateResponsePairById: (
    responsePairId: string,
    updater: QuestionUpdater,
  ) => void,
  updatePlacementForAnswer: (
    question: QuestionnaireResponsePair,
    updates: Partial<UserPlacement>,
    immediate: boolean,
  ) => QuestionnaireResponsePair,
  debouncedUpdatePlacement: (
    qId: string,
    rpId: string,
    placement: AnswerPlacementPayload,
  ) => void,
  handleInputFocus: (questionId: string, optionBoxId?: string) => void,
) => {
  const handleTextChange = useCallback(
    (responsePairId: string, nextValue: string) => {
      updateResponsePairById(responsePairId, (question) => ({
        ...question,
        response: nextValue,
        manuallyEdited: true,
      }))

      updateResponsePairById(responsePairId, (question) =>
        updatePlacementForAnswer(question, { text: nextValue }, false),
      )
    },
    [updatePlacementForAnswer, updateResponsePairById],
  )

  const handleOptionChange = useCallback(
    (responsePairId: string, nextValue: string) => {
      updateResponsePairById(responsePairId, (question) => ({
        ...question,
        response: nextValue,
        manuallyEdited: true,
      }))

      updateResponsePairById(responsePairId, (question) => {
        const newQuestion = updatePlacementForAnswer(
          question,
          { text: "" },
          false,
        )

        const optionsPlacements = getOptionsPlacements(
          question,
          newQuestion.pdfPlacement?.userPlacement,
        )

        debouncedUpdatePlacement(questionnaireId, question.id, {
          ...toApiPlacement(newQuestion.pdfPlacement!.userPlacement!),
          text: nextValue,
          options_placements: optionsPlacements,
        })

        return newQuestion
      })

      const currentQuestion = questionnaireData.sections
        .flatMap((s) => s.responsePairs)
        .find((rp) => rp.id === responsePairId)

      const options = currentQuestion?.pdfPlacement?.extraction?.optionsWithBbox
      const idx =
        options?.findIndex(
          (o) => normalizeOptionText(o.text) === normalizeOptionText(nextValue),
        ) ?? -1

      if (idx >= 0) {
        handleInputFocus(responsePairId, `${responsePairId},radio,${idx}`)
      } else {
        handleInputFocus(responsePairId)
      }
    },
    [
      debouncedUpdatePlacement,
      handleInputFocus,
      questionnaireData.sections,
      questionnaireId,
      updatePlacementForAnswer,
      updateResponsePairById,
    ],
  )

  return {
    handleTextChange,
    handleOptionChange,
  }
}
